import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';
import EmpresasService from '../../services/EmpresasService';
import ArquivoRetornoService from '../../services/ArquivoRetornoService';
import ConsultaLancamentosService, {
    IConsultaLancamentosFiltro,
    IConsultaLancamentoItem,
    ConsultaSortField,
} from '../../services/ConsultaLancamentosService';
import { IArquivoRetorno } from '../../interfaces/IArquivoRetorno';
import {
    agruparArquivosPorOperacao,
    baixarGrupoComoZip,
    GrupoArquivoRetorno,
} from '../../utils/arquivoRetornoGrupos';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
const PER_PAGE_OPCOES = [50, 100, 200];
const FILTRO_VAZIO: IConsultaLancamentosFiltro = {};

interface Coluna {
    campo: ConsultaSortField;
    titulo: string;
    minWidth: number;
}

const COLUNAS: Coluna[] = [
    { campo: 'conta', titulo: 'Conta Contábil', minWidth: 130 },
    { campo: 'descricao', titulo: 'Descrição da Conta', minWidth: 200 },
    { campo: 'data', titulo: 'Data Lançamento', minWidth: 150 },
    { campo: 'historico', titulo: 'Histórico', minWidth: 280 },
    { campo: 'valor', titulo: 'Valor', minWidth: 150 },
    { campo: 'tipo', titulo: 'Tipo', minWidth: 90 },
    { campo: 'status', titulo: 'Status', minWidth: 160 },
    { campo: 'chaveVinculo', titulo: 'Chave Vínculo', minWidth: 150 },
    { campo: 'chaveVinculoMatch', titulo: 'Chave Vínculo Match', minWidth: 170 },
];

function statusBadge(arquivo: IArquivoRetorno): { label: string; color: string; bg: string } {
    if (arquivo.erro) return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (arquivo.processando) return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (arquivo.processou) return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

function ConsultaLancamentos() {
    const history = useHistory();
    const location = useLocation();
    const { classes } = useLayout();

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const empresaId = Number(queryParams.get('empresaId')) || 0;
    const esteiraId = Number(queryParams.get('esteiraId')) || 0;

    const [periodoLabel, setPeriodoLabel] = useState<string>('');

    const [filtros, setFiltros] = useState<IConsultaLancamentosFiltro>(FILTRO_VAZIO);
    const [sortField, setSortField] = useState<ConsultaSortField>('data');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [perPage, setPerPage] = useState(PER_PAGE_OPCOES[0]);

    const [items, setItems] = useState<IConsultaLancamentoItem[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [statusDisponiveis, setStatusDisponiveis] = useState<string[]>([]);

    const [exportando, setExportando] = useState(false);
    const [arquivos, setArquivos] = useState<IArquivoRetorno[]>([]);
    const [baixando, setBaixando] = useState<number | null>(null);
    const [baixandoGrupo, setBaixandoGrupo] = useState<number | null>(null);
    const [progressoGrupo, setProgressoGrupo] = useState<{ atual: number; total: number } | null>(null);

    const grupos = useMemo(() => agruparArquivosPorOperacao(arquivos), [arquivos]);

    // --- carregamento inicial de contexto ---
    useEffect(() => {
        if (!empresaId || !esteiraId) return;
        EmpresasService.obterEsteiras(empresaId).then((esteiras: any[]) => {
            const e = esteiras.find(x => x.id === esteiraId);
            if (e) setPeriodoLabel(`${e.periodoInicioString ?? moment(e.periodoInicio).format('MM/YYYY')} - ${e.periodoFimString ?? moment(e.periodoFim).format('MM/YYYY')}`);
        }).catch(() => { /* silencioso */ });
    }, [empresaId, esteiraId]);

    useEffect(() => {
        if (!esteiraId) return;
        ConsultaLancamentosService.statusDistintos(esteiraId)
            .then(setStatusDisponiveis)
            .catch(() => setStatusDisponiveis([]));
    }, [esteiraId]);

    // --- consulta da grade ---
    const carregar = useCallback((pg: number, f: IConsultaLancamentosFiltro, sf: ConsultaSortField, sd: 'asc' | 'desc', pp: number) => {
        if (!esteiraId) return;
        setLoading(true);
        ConsultaLancamentosService.consultar(esteiraId, f, sf, sd, pg, pp)
            .then(resp => {
                setItems(resp.items);
                setTotal(resp.total);
                setTotalPages(resp.totalPages || 1);
                setPage(resp.page || pg);
            })
            .catch(() => {
                setItems([]);
                setTotal(0);
                setTotalPages(1);
            })
            .finally(() => setLoading(false));
    }, [esteiraId]);

    // debounce nos filtros — recarrega sempre da página 1
    const primeiraRenderRef = useRef(true);
    useEffect(() => {
        if (!esteiraId) return;
        const t = setTimeout(() => carregar(1, filtros, sortField, sortDir, perPage), primeiraRenderRef.current ? 0 : 400);
        primeiraRenderRef.current = false;
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros, sortField, sortDir, perPage, esteiraId]);

    // --- lista de exports + polling ---
    const buscarArquivos = useCallback(async () => {
        if (!esteiraId) return;
        try {
            setArquivos(await ConsultaLancamentosService.listarXlsx(esteiraId));
        } catch { /* silencioso */ }
    }, [esteiraId]);

    useEffect(() => { buscarArquivos(); }, [buscarArquivos]);

    useEffect(() => {
        if (!arquivos.some(a => a.processando)) return;
        const interval = setInterval(buscarArquivos, 8000);
        return () => clearInterval(interval);
    }, [arquivos, buscarArquivos]);

    // --- handlers de filtro ---
    const setF = (patch: Partial<IConsultaLancamentosFiltro>) => setFiltros(prev => ({ ...prev, ...patch }));
    const limparFiltros = () => setFiltros(FILTRO_VAZIO);

    const toggleSort = (campo: ConsultaSortField) => {
        if (sortField === campo) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortField(campo); setSortDir('asc'); }
    };

    const toggleStatus = (s: string) => setFiltros(prev => {
        const atual = new Set(prev.status ?? []);
        atual.has(s) ? atual.delete(s) : atual.add(s);
        return { ...prev, status: atual.size ? Array.from(atual) : undefined };
    });

    const filtroAtivo = useMemo(
        () => Object.entries(filtros).some(([, v]) => (Array.isArray(v) ? v.length > 0 : v != null && v !== '')),
        [filtros],
    );

    // --- export ---
    const handleExportar = async () => {
        if (total === 0) return;
        setExportando(true);
        try {
            await ConsultaLancamentosService.gerarXlsx(esteiraId, filtros);
            Swal.fire({
                text: 'Geração do XLSX iniciada! O arquivo ficará disponível para download quando concluído.',
                icon: 'success', timer: 4000, showConfirmButton: false,
            });
            await buscarArquivos();
        } catch (e: any) {
            Swal.fire({
                text: e?.response?.data?.message ?? 'Erro ao iniciar a exportação.',
                icon: 'error', timer: 3500, showConfirmButton: false,
            });
        } finally {
            setExportando(false);
        }
    };

    const handleDownload = async (arquivo: IArquivoRetorno) => {
        if (!arquivo.processou || arquivo.erro) return;
        setBaixando(arquivo.id);
        try {
            const url = await ArquivoRetornoService.obterUrlDownload(arquivo.id);
            const link = document.createElement('a');
            link.href = url;
            link.download = arquivo.nomeArquivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            Swal.fire({ text: 'Erro ao gerar link de download.', icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setBaixando(null);
        }
    };

    const handleBaixarGrupo = async (grupo: GrupoArquivoRetorno) => {
        if (!grupo.operacaoId || !grupo.completo) return;
        setBaixandoGrupo(grupo.operacaoId);
        setProgressoGrupo({ atual: 0, total: grupo.arquivos.filter(a => a.processou && !a.erro).length });
        try {
            const nomeBase = grupo.arquivos[0]?.nomeArquivo.replace(/_parte\d+/i, '').replace(/\.xlsx$/i, '') || 'consulta_lancamentos';
            await baixarGrupoComoZip(grupo, `${nomeBase}_completo.zip`, (atual, tot) => setProgressoGrupo({ atual, total: tot }));
        } catch {
            Swal.fire({ text: 'Erro ao baixar as partes.', icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setBaixandoGrupo(null);
            setProgressoGrupo(null);
        }
    };

    if (!empresaId || !esteiraId) {
        return (
            <>
                <PageTitle breadcrumbs={[]}>Consultar Lançamentos</PageTitle>
                <div className='card'>
                    <div className='card-body text-center text-muted py-10'>
                        Abra esta tela pelo botão <b>Consultar Lançamentos</b> em <b>Andamento de Contas</b>, com uma empresa e um período selecionados.
                        <div className='mt-4'>
                            <button className='btn btn-sm btn-primary' onClick={() => history.push('/andamento-contas')}>Ir para Andamento de Contas</button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const inicio = total === 0 ? 0 : (page - 1) * perPage + 1;
    const fim = Math.min(page * perPage, total);
    const setinha = (campo: ConsultaSortField) => (sortField !== campo ? '' : sortDir === 'asc' ? ' ▲' : ' ▼');

    return (
        <>
            <PageTitle breadcrumbs={[]}>Consultar Lançamentos</PageTitle>
            <div className='toolbar' id='kt_toolbar'>
                <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                    <DefaultTitle />
                </div>
            </div>

            <div className='card mb-4'>
                <div className='card-header border-0 pt-6'>
                    <div className='card-title flex-column'>
                        <span className='fw-bold fs-5'>Lançamentos da esteira</span>
                        {periodoLabel && <span className='text-muted fs-7'>Período {periodoLabel}</span>}
                    </div>
                    <div className='card-toolbar gap-2'>
                        <button className='btn btn-sm btn-light' onClick={() => history.push(`/andamento-contas/${empresaId}/${esteiraId}`)}>‹ Voltar ao Mapa</button>
                        {filtroAtivo && (
                            <button className='btn btn-sm btn-light-secondary' onClick={limparFiltros} disabled={loading}>Limpar filtros</button>
                        )}
                        <button
                            className='btn btn-sm btn-success d-flex align-items-center gap-2'
                            onClick={handleExportar}
                            disabled={exportando || loading || total === 0}
                            title={total === 0 ? 'Nenhum lançamento no filtro atual' : 'Exporta em XLSX exatamente as linhas do filtro atual'}
                        >
                            {exportando ? <span className='spinner-border spinner-border-sm' /> : <i className='fas fa-file-excel' />}
                            {exportando ? 'Gerando...' : 'Exportar filtrado (XLSX)'}
                        </button>
                    </div>
                </div>

                <div className='card-body pt-2'>
                    <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
                        <span className='fs-7 text-muted'>
                            {loading ? 'Carregando...' : <>Mostrando <b>{inicio}</b>–<b>{fim}</b> de <b>{total.toLocaleString('pt-BR')}</b> lançamento{total !== 1 ? 's' : ''}</>}
                        </span>
                        <div className='d-flex align-items-center gap-2'>
                            <span className='fs-8 text-muted'>Linhas por página</span>
                            <select
                                className='form-select form-select-sm w-auto'
                                value={perPage}
                                onChange={e => setPerPage(Number(e.target.value))}
                            >
                                {PER_PAGE_OPCOES.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className='table-responsive' style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                        <table className='table table-row-bordered table-row-gray-300 align-middle gs-3 gy-2' style={{ minWidth: 1400 }}>
                            <thead className='sticky-top bg-white' style={{ zIndex: 1 }}>
                                <tr className='fw-bold text-muted'>
                                    {COLUNAS.map(c => (
                                        <th
                                            key={c.campo}
                                            style={{ minWidth: c.minWidth, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            onClick={() => toggleSort(c.campo)}
                                            title='Ordenar'
                                        >
                                            {c.titulo}{setinha(c.campo)}
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {/* Conta */}
                                    <th>
                                        <input className='form-control form-control-sm' placeholder='contém'
                                            value={filtros.contaContabil ?? ''} onChange={e => setF({ contaContabil: e.target.value })} />
                                    </th>
                                    {/* Descrição */}
                                    <th>
                                        <input className='form-control form-control-sm' placeholder='contém'
                                            value={filtros.descricaoConta ?? ''} onChange={e => setF({ descricaoConta: e.target.value })} />
                                    </th>
                                    {/* Data */}
                                    <th>
                                        <div className='d-flex flex-column gap-1'>
                                            <input type='date' className='form-control form-control-sm'
                                                value={filtros.dataInicio ?? ''} onChange={e => setF({ dataInicio: e.target.value })} />
                                            <input type='date' className='form-control form-control-sm'
                                                value={filtros.dataFim ?? ''} onChange={e => setF({ dataFim: e.target.value })} />
                                        </div>
                                    </th>
                                    {/* Histórico */}
                                    <th>
                                        <input className='form-control form-control-sm' placeholder='contém'
                                            value={filtros.historico ?? ''} onChange={e => setF({ historico: e.target.value })} />
                                    </th>
                                    {/* Valor */}
                                    <th>
                                        <div className='d-flex flex-column gap-1'>
                                            <input type='number' step='0.01' className='form-control form-control-sm' placeholder='mín'
                                                value={filtros.valorMin ?? ''} onChange={e => setF({ valorMin: e.target.value })} />
                                            <input type='number' step='0.01' className='form-control form-control-sm' placeholder='máx'
                                                value={filtros.valorMax ?? ''} onChange={e => setF({ valorMax: e.target.value })} />
                                        </div>
                                    </th>
                                    {/* Tipo */}
                                    <th>
                                        <select className='form-select form-select-sm'
                                            value={filtros.tipo ?? ''} onChange={e => setF({ tipo: e.target.value || undefined })}>
                                            <option value=''>Todos</option>
                                            <option value='C'>C</option>
                                            <option value='D'>D</option>
                                        </select>
                                    </th>
                                    {/* Status */}
                                    <th>
                                        <details className='position-relative'>
                                            <summary className='form-select form-select-sm' style={{ listStyle: 'none', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {filtros.status?.length ? `${filtros.status.length} selecionado(s)` : 'Todos'}
                                            </summary>
                                            <div className='position-absolute bg-white border rounded shadow-sm p-2 mt-1'
                                                style={{ zIndex: 5, minWidth: 180, maxHeight: 220, overflowY: 'auto' }}>
                                                {statusDisponiveis.length === 0 && <span className='text-muted fs-8'>—</span>}
                                                {statusDisponiveis.map(s => (
                                                    <label key={s} className='d-flex align-items-center gap-2 fs-8 py-1' style={{ cursor: 'pointer' }}>
                                                        <input type='checkbox' checked={filtros.status?.includes(s) ?? false} onChange={() => toggleStatus(s)} />
                                                        {s}
                                                    </label>
                                                ))}
                                            </div>
                                        </details>
                                    </th>
                                    {/* Chave Vínculo */}
                                    <th>
                                        <div className='d-flex flex-column gap-1'>
                                            <input className='form-control form-control-sm' placeholder='contém'
                                                value={filtros.chaveVinculo ?? ''} onChange={e => setF({ chaveVinculo: e.target.value })} />
                                            <select className='form-select form-select-sm'
                                                value={filtros.chaveVinculoEstado ?? ''} onChange={e => setF({ chaveVinculoEstado: e.target.value || undefined })}>
                                                <option value=''>Qualquer</option>
                                                <option value='vazio'>Vazio</option>
                                                <option value='preenchido'>Preenchido</option>
                                            </select>
                                        </div>
                                    </th>
                                    {/* Chave Vínculo Match */}
                                    <th>
                                        <div className='d-flex flex-column gap-1'>
                                            <input className='form-control form-control-sm' placeholder='contém'
                                                value={filtros.chaveVinculoMatch ?? ''} onChange={e => setF({ chaveVinculoMatch: e.target.value })} />
                                            <select className='form-select form-select-sm'
                                                value={filtros.chaveVinculoMatchEstado ?? ''} onChange={e => setF({ chaveVinculoMatchEstado: e.target.value || undefined })}>
                                                <option value=''>Qualquer</option>
                                                <option value='vazio'>Vazio</option>
                                                <option value='preenchido'>Preenchido</option>
                                            </select>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan={COLUNAS.length} className='text-center text-muted py-8 fs-6'>
                                            {loading ? 'Carregando...' : 'Nenhum lançamento para o filtro atual.'}
                                        </td>
                                    </tr>
                                ) : (
                                    items.map(l => (
                                        <tr key={l.id}>
                                            <td className='fs-8 fw-semibold'>{l.contaContabil}</td>
                                            <td className='fs-8'>{l.descricaoConta || '—'}</td>
                                            <td className='fs-8'>{l.dataLancamento ? moment(l.dataLancamento).format('DD/MM/YYYY') : '—'}</td>
                                            <td className='fs-8' style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.historico ?? undefined}>{l.historico || '—'}</td>
                                            <td className='fs-8 text-end fw-semibold'>{l.valor != null ? BRL.format(l.valor) : '—'}</td>
                                            <td className='fs-8'>{l.tipo || '—'}</td>
                                            <td className='fs-8'>{l.status || '—'}</td>
                                            <td className='fs-8'>{l.chaveVinculo || '—'}</td>
                                            <td className='fs-8'>{l.chaveVinculoMatch || '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mt-3'>
                            <span className='fs-7 text-muted'>Página {page} de {totalPages}</span>
                            <div className='d-flex gap-1'>
                                <button className='btn btn-sm btn-light' disabled={page <= 1 || loading}
                                    onClick={() => carregar(page - 1, filtros, sortField, sortDir, perPage)}>‹ Anterior</button>
                                <button className='btn btn-sm btn-light' disabled={page >= totalPages || loading}
                                    onClick={() => carregar(page + 1, filtros, sortField, sortDir, perPage)}>Próxima ›</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Painel de exports */}
            <div className='card'>
                <div className='card-header border-0 pt-5'>
                    <div className='card-title'><span className='fw-bold fs-6'>Exportações filtradas</span></div>
                    <div className='card-toolbar'>
                        <button className='btn btn-sm btn-light-secondary d-flex align-items-center gap-1' onClick={buscarArquivos}>
                            <i className='fas fa-sync-alt' style={{ fontSize: 10 }} /> Atualizar
                        </button>
                    </div>
                </div>
                <div className='card-body pt-0'>
                    {grupos.length === 0 ? (
                        <div className='text-center text-muted py-6 fs-7'>Nenhum arquivo gerado ainda. Use “Exportar filtrado (XLSX)”.</div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mb-0'>
                                <thead>
                                    <tr className='fw-bold text-muted' style={{ fontSize: 11 }}>
                                        <th>Arquivo</th><th>Gerado em</th><th className='text-center'>Status</th><th className='text-center'>Download</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grupos.map(grupo => (
                                        <Fragment key={grupo.operacaoId ?? `x-${grupo.arquivos[0].id}`}>
                                            {grupo.arquivos.length > 1 && (
                                                <tr>
                                                    <td colSpan={4} style={{ backgroundColor: '#eef1f5' }}>
                                                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 py-1'>
                                                            <span style={{ fontSize: 10 }}>
                                                                <i className='fas fa-layer-group me-1' />
                                                                Export dividido em {grupo.arquivos.length} partes
                                                                {grupo.temErro && <span style={{ color: '#d9534f' }} className='ms-2'>(uma ou mais partes falharam)</span>}
                                                            </span>
                                                            <button
                                                                className='btn btn-sm btn-light-primary d-flex align-items-center gap-1'
                                                                disabled={!grupo.completo || baixandoGrupo === grupo.operacaoId}
                                                                onClick={() => handleBaixarGrupo(grupo)}
                                                                style={{ fontSize: 10 }}
                                                            >
                                                                {baixandoGrupo === grupo.operacaoId
                                                                    ? <><span className='spinner-border spinner-border-sm' />{progressoGrupo ? ` ${progressoGrupo.atual}/${progressoGrupo.total}` : ' Baixando...'}</>
                                                                    : !grupo.completo ? 'Aguardando conclusão...' : <><i className='fas fa-file-archive' /> Baixar tudo (.zip)</>}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {grupo.arquivos.map((arquivo, idx) => {
                                                const st = statusBadge(arquivo);
                                                return (
                                                    <tr key={arquivo.id}>
                                                        <td style={{ fontSize: 11, maxWidth: 260 }}>
                                                            <span title={arquivo.nomeArquivo} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {grupo.arquivos.length > 1 && <span className='text-muted me-1'>[Parte {idx + 1}]</span>}
                                                                {arquivo.nomeArquivo}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{moment(arquivo.dataArquivo).format('DD/MM/YYYY HH:mm')}</td>
                                                        <td className='text-center'>
                                                            <span style={{ fontSize: 9, fontWeight: 'bold', color: st.color, backgroundColor: st.bg, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                                                                {st.label}
                                                            </span>
                                                        </td>
                                                        <td className='text-center'>
                                                            {arquivo.processou && !arquivo.erro ? (
                                                                <button className='btn btn-icon btn-sm btn-light-success' title='Baixar'
                                                                    onClick={() => handleDownload(arquivo)} disabled={baixando === arquivo.id}>
                                                                    {baixando === arquivo.id ? <span className='spinner-border spinner-border-sm' /> : <i className='fas fa-download fs-6' />}
                                                                </button>
                                                            ) : <span className='text-muted'>—</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default ConsultaLancamentos;
